import type { ApiToken, ApiTokenScope, CreateApiTokenResponse } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, KeyRound, Plug, Plus } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { CodeBlock, CopyButton } from '@/components/code-block';
import { Confirm } from '@/components/confirm';
import { Field } from '@/components/field';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { formatAbsolute } from '@/lib/format';
import { useApiMutation, useWorkspace } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

const ALL_WORKSPACES = '__all__';
const SCOPE_LABEL: Record<ApiTokenScope, string> = { read: t('Read'), write: t('Write') };
const EXPIRY_OPTIONS = [
  { value: '30', label: t('30 days') },
  { value: '90', label: t('90 days') },
  { value: '365', label: t('365 days') },
  { value: 'never', label: t('Never') },
];

function isExpired(token: ApiToken): boolean {
  return !!token.expiresAt && new Date(token.expiresAt).getTime() < Date.now();
}

/** "API & MCP": personal API tokens for Deskworks's REST API (/api) and its MCP server. Tokens belong to the current Account. */
export function McpAccessSection({ workspaceId }: { workspaceId: string }) {
  const workspace = useWorkspace(workspaceId);
  const accountId = workspace.data?.accountId ?? '';
  const tokens = useQuery({
    queryKey: qk.apiTokens(accountId),
    queryFn: () => api.apiTokens.list(accountId),
    enabled: !!accountId,
  });
  const revoke = useApiMutation((id: string) => api.apiTokens.revoke(id), {
    invalidate: [qk.apiTokens(accountId)],
    success: t('Token revoked'),
  });

  return (
    <Section
      icon={<KeyRound />}
      title={t('Access tokens')}
      description={t('With an access token you reach Deskworks from Claude Code, Claude Desktop, Cursor & co. via MCP, and from your own scripts via the REST API (/api).')}
      actions={
        accountId && (
          <CreateTokenDialog accountId={accountId} currentWorkspaceId={workspaceId}>
            <Button size="sm">
              <Plus /> {t('Create token')}
            </Button>
          </CreateTokenDialog>
        )
      }
    >
      {workspace.isError ? (
        <ErrorState error={workspace.error} onRetry={() => void workspace.refetch()} />
      ) : !accountId || tokens.isPending ? (
        <LoadingRows rows={2} />
      ) : tokens.isError ? (
        <ErrorState error={tokens.error} onRetry={() => void tokens.refetch()} />
      ) : tokens.data.length === 0 ? (
        <EmptyState
          compact
          title={t('No access tokens yet')}
          description={t('Create a token and add Deskworks as an MCP server in your AI tool, or call the REST API with it. Then you can, for example, run desks or give approvals from there.')}
        />
      ) : (
        <ul className="-mx-5 -mb-5 divide-y border-t">
          {[...tokens.data]
            // Active tokens first; revoked/expired ones sink to the bottom (order otherwise as returned).
            .sort((a, b) => Number(!!a.revokedAt || isExpired(a)) - Number(!!b.revokedAt || isExpired(b)))
            .map((tok) => {
            const revoked = !!tok.revokedAt;
            const expired = !revoked && isExpired(tok);
            return (
              <li key={tok.id} className={cn('flex flex-wrap items-start gap-3 px-5 py-3 sm:flex-nowrap', (revoked || expired) && 'opacity-60')}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{tok.name}</span>
                    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{tok.prefix}…</code>
                    {tok.scopes.map((s) => (
                      <Badge key={s} tone={s === 'write' ? 'amber' : 'outline'}>
                        {SCOPE_LABEL[s] ?? s}
                      </Badge>
                    ))}
                    {revoked && <Badge tone="red">{t('Revoked')}</Badge>}
                    {expired && <Badge tone="muted">{t('Expired')}</Badge>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    <span>{tok.workspaceId ? t('Workspace: {name}', { name: tok.workspaceName ?? tok.workspaceId }) : t('All workspaces')}</span>
                    <span>{t('Created by {email}', { email: tok.userEmail })}</span>
                    <span>{tr('Created {time}', { time: <RelativeTime value={tok.createdAt} /> })}</span>
                    <span>{tr('Last used: {time}', { time: <RelativeTime value={tok.lastUsedAt} fallback={t('never')} /> })}</span>
                    {revoked ? (
                      <span>{tr('Revoked {time}', { time: <RelativeTime value={tok.revokedAt} /> })}</span>
                    ) : (
                      <span title={tok.expiresAt ? formatAbsolute(tok.expiresAt) : undefined}>
                        {tok.expiresAt
                          ? expired
                            ? tr('Expired {time}', { time: <RelativeTime value={tok.expiresAt} /> })
                            : tr('Expires {time}', { time: <RelativeTime value={tok.expiresAt} /> })
                          : t('Never expires')}
                      </span>
                    )}
                  </div>
                </div>
                {!revoked && (
                  <Confirm
                    title={t('Revoke token “{name}”?', { name: tok.name })}
                    description={t('All tools using this token immediately lose access to Deskworks. This can’t be undone.')}
                    confirmLabel={t('Revoke')}
                    destructive
                    onConfirm={() => revoke.mutateAsync(tok.id)}
                  >
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                      {t('Revoke')}
                    </Button>
                  </Confirm>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

/**
 * "Connected apps": Claude, ChatGPT and other hosted MCP clients that signed in by OAuth at the MCP address. Each
 * reaches every workspace of the Account with the permissions of the person who allowed it.
 */
export function McpAppsSection({ workspaceId }: { workspaceId: string }) {
  const workspace = useWorkspace(workspaceId);
  const accountId = workspace.data?.accountId ?? '';
  const apps = useQuery({
    queryKey: qk.mcpApps(accountId),
    queryFn: () => api.mcpApps.list(accountId),
    enabled: !!accountId,
  });
  const disconnect = useApiMutation((id: string) => api.mcpApps.disconnect(id), {
    invalidate: [qk.mcpApps(accountId)],
    success: t('App disconnected'),
  });

  return (
    <Section
      icon={<Plug />}
      title={t('Connected apps')}
      description={t('Claude, ChatGPT and other apps connect by signing in: add Deskworks as a custom connector with this address. When signing in you choose the account and whether the app may write.')}
    >
      {workspace.isError ? (
        <ErrorState error={workspace.error} onRetry={() => void workspace.refetch()} />
      ) : !accountId || apps.isPending ? (
        <LoadingRows rows={2} />
      ) : apps.isError ? (
        <ErrorState error={apps.error} onRetry={() => void apps.refetch()} />
      ) : (
        <div className="grid gap-4">
          <div className="flex min-w-0 items-center gap-1 rounded-md border bg-muted/40 py-1 pr-1 pl-3">
            <code className="min-w-0 flex-1 truncate font-mono text-sm">{apps.data.mcpUrl}</code>
            <CopyButton value={apps.data.mcpUrl} label={t('Copy address')} />
          </div>
          {apps.data.apps.length === 0 ? (
            <EmptyState compact title={t('No apps connected yet')} />
          ) : (
            <ul className="-mx-5 -mb-5 divide-y border-t">
              {apps.data.apps.map((app) => (
                <li key={app.id} className="flex flex-wrap items-start gap-3 px-5 py-3 sm:flex-nowrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{app.clientName}</span>
                      {app.scopes.map((s) => (
                        <Badge key={s} tone={s === 'write' ? 'amber' : 'outline'}>
                          {SCOPE_LABEL[s] ?? s}
                        </Badge>
                      ))}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span>{t('All workspaces')}</span>
                      <span>{t('Allowed by {email}', { email: app.userEmail })}</span>
                      <span>{tr('Connected {time}', { time: <RelativeTime value={app.createdAt} /> })}</span>
                      <span>{tr('Last used: {time}', { time: <RelativeTime value={app.lastUsedAt} fallback={t('never')} /> })}</span>
                    </div>
                  </div>
                  <Confirm
                    title={t('Disconnect “{name}”?', { name: app.clientName })}
                    description={t('The app immediately loses access to Deskworks. To use it again, connect it again from the app.')}
                    confirmLabel={t('Disconnect')}
                    destructive
                    onConfirm={() => disconnect.mutateAsync(app.id)}
                  >
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                      {t('Disconnect')}
                    </Button>
                  </Confirm>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Section>
  );
}

function CreateTokenDialog({
  accountId,
  currentWorkspaceId,
  children,
}: {
  accountId: string;
  currentWorkspaceId: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [workspace, setWorkspace] = useState(ALL_WORKSPACES);
  const [write, setWrite] = useState(true);
  const [expiry, setExpiry] = useState('90');
  const [created, setCreated] = useState<CreateApiTokenResponse | null>(null);

  const workspaces = useQuery({
    queryKey: qk.accountWorkspaces(accountId),
    queryFn: () => api.accounts.workspaces(accountId),
    enabled: open,
  });

  const create = useApiMutation(
    () =>
      api.apiTokens.create(accountId, {
        name: name.trim(),
        workspaceId: workspace === ALL_WORKSPACES ? null : workspace,
        scopes: write ? ['read', 'write'] : ['read'],
        expiresInDays: expiry === 'never' ? null : Number(expiry),
      }),
    {
      invalidate: [qk.apiTokens(accountId)],
      onSuccess: (res) => setCreated(res),
    },
  );

  const reset = () => {
    setName('');
    setWorkspace(ALL_WORKSPACES);
    setWrite(true);
    setExpiry('90');
    setCreated(null);
  };

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) reset();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="sm:max-w-2xl"
        // Don't lose the one-time token by an accidental click outside.
        onInteractOutside={(e) => created && e.preventDefault()}
      >
        {created ? (
          <TokenCreated response={created} onDone={() => onOpenChange(false)} />
        ) : (
          <form onSubmit={onSubmit} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>{t('Create access token')}</DialogTitle>
              <DialogDescription>
                {t('The token belongs to you personally and gives an AI tool or a script access to Deskworks — with your permissions, limited to what you choose here. For a website or an app, create a Data API key under Database instead.')}
              </DialogDescription>
            </DialogHeader>
            <Field label={t('Name')} hint={t('So you know later what it’s for.')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Claude Code on my laptop')} required autoFocus />
            </Field>
            <Field label={t('Workspace')} hint={t('Limit the token to one workspace — or allow all workspaces you have access to. A token for one workspace works only over MCP, not on /api.')}>
              <Select value={workspace} onValueChange={setWorkspace}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_WORKSPACES}>{t('All workspaces')}</SelectItem>
                  {(workspaces.data ?? []).map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                      {w.id === currentWorkspaceId && ` (${t('current')})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="grid gap-2">
              <Label>{t('Permissions')}</Label>
              <label className="flex items-start gap-2.5 rounded-md border px-3 py-2.5">
                <Checkbox checked disabled className="mt-0.5" />
                <span className="text-sm">
                  <span className="font-medium">{t('Read')}</span>
                  <span className="block text-xs text-muted-foreground">{t('View desks, work items, agents and open approvals. Always included.')}</span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5">
                <Checkbox checked={write} onChange={(e) => setWrite(e.target.checked)} className="mt-0.5" />
                <span className="text-sm">
                  <span className="font-medium">{t('Write')}</span>
                  <span className="block text-xs text-muted-foreground">
                    {t('Create and run desks, create work items, send messages, give approvals.')}
                  </span>
                </span>
              </label>
            </div>
            <Field label={t('Expires after')}>
              <Select value={expiry} onValueChange={setExpiry}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('Cancel')}
              </Button>
              <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
                {t('Create token')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TokenCreated({ response, onDone }: { response: CreateApiTokenResponse; onDone: () => void }) {
  const path = response.mcpPath.startsWith('/') ? response.mcpPath : `/${response.mcpPath}`;
  const url = /^https?:\/\//.test(response.mcpPath) ? response.mcpPath : `${window.location.origin}${path}`;
  const token = response.token;
  const claudeCode = `claude mcp add --transport http deskworks ${url} --header "Authorization: Bearer ${token}"`;
  const rest = `curl ${window.location.origin}/api/accounts/${response.apiToken.accountId}/workspaces \\\n  -H "Authorization: Bearer ${token}"`;
  const json = JSON.stringify(
    { mcpServers: { deskworks: { type: 'http', url, headers: { Authorization: `Bearer ${token}` } } } },
    null,
    2,
  );

  return (
    <div className="grid min-w-0 gap-4">
      <DialogHeader>
        <DialogTitle>{t('Token created')}</DialogTitle>
        <DialogDescription>{t('“{name}” is ready. Now set up your AI tool or script.', { name: response.apiToken.name })}</DialogDescription>
      </DialogHeader>
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        {t('Copy the token now — it’s only shown this one time. If you lose it, revoke it and create a new one.')}
      </div>
      <div className="grid min-w-0 gap-1.5">
        <Label>{t('Your token')}</Label>
        <div className="flex min-w-0 items-center gap-1 rounded-md border bg-muted/40 py-1 pr-1 pl-3">
          <code className="min-w-0 flex-1 truncate font-mono text-sm" title={token}>
            {token}
          </code>
          <CopyButton value={token} label={t('Copy token')} />
        </div>
      </div>
      <div className="grid min-w-0 gap-1.5">
        <Label>Claude Code</Label>
        <p className="text-xs text-muted-foreground">{t('Run in your terminal:')}</p>
        <CodeBlock value={claudeCode} maxHeight="max-h-40" showCopy />
      </div>
      <div className="grid min-w-0 gap-1.5">
        <Label>{t('Claude Desktop, Cursor & others (JSON configuration)')}</Label>
        <p className="text-xs text-muted-foreground">{t('Paste into your tool’s MCP configuration:')}</p>
        <CodeBlock value={json} maxHeight="max-h-80" showCopy />
      </div>
      {!response.apiToken.workspaceId && (
        <div className="grid min-w-0 gap-1.5">
          <Label>{t('REST API')}</Label>
          <p className="text-xs text-muted-foreground">{t('Every route of the API, as you in this account:')}</p>
          <CodeBlock value={rest} maxHeight="max-h-40" showCopy />
        </div>
      )}
      <DialogFooter>
        <Button onClick={onDone}>{t('Done — I’ve saved the token')}</Button>
      </DialogFooter>
    </div>
  );
}
